import cloneDeep from "lodash/cloneDeep";
import uniqBy from "lodash/uniqBy";
import { IRange } from "monaco-editor";
import {
  CodeLoc,
  ErrorType,
  LegacyRule,
  PatternConfigLocation,
  Result,
  RuleVisibilities,
  RunTiming,
  SemgrepErrors,
  Snippet,
  TestCaseHighlight,
} from "../../types";
import {
  DEFAULT_RULE,
  DEFAULT_RULE_VISIBILITY,
  LANG_COMMENT_SYNTAX_MAP,
  makeResultKey,
} from "../../util";
import { recoverBrowserSecurityError } from "../../utils/storage";
import { SESSION_CONTENTS } from "./constants";
import { ruleFromYAML } from "./parseYaml";
import { Highlight } from "./types";
import yaml from "yaml";
import { schemaErrorToParseError } from "../../errors";
import { AppToaster } from "../../singletons";
import { run as apiRun } from "../../api";
import { validateYaml } from "../../yaml";
import { yamlFromRule } from "./serializeYaml";
import { VISIBLE_LANGUAGES } from "../../lang";
export interface RunResult {
  results?: Result[];
  errors?: SemgrepErrors;
  timing?: RunTiming;
  imageId?: string;
  runTimeS: number;
  errorMsg?: string;
  errorType?: ErrorType;
  numTestFailures?: number;
}

export const EMPTY_RUN_RESULT: RunResult = {
  results: undefined,
  errors: undefined,
  timing: undefined,
  imageId: undefined,
  runTimeS: 0.0,
  errorMsg: undefined,
  errorType: undefined,
  numTestFailures: undefined,
};

export const createSnippetUrl = (id: string, searchParams: string) => {
  const urlParams = new URLSearchParams(searchParams);
  // remove registry id if present
  urlParams.delete("registry");
  const newQs = urlParams.toString() === "" ? "" : `?${urlParams.toString()}`;
  return `/s/${id}${newQs}`;
};

/**
 * Saving and restoring from local storage
 */

export const saveSessionToLocalStorage = (
  pattern: string,
  language: string,
  target: string
) => {
  const snippet: Snippet = { pattern, language, target };
  const snippet_str: string = JSON.stringify(snippet);
  recoverBrowserSecurityError(() =>
    localStorage.setItem(SESSION_CONTENTS, snippet_str)
  );
};
export const restoreSessionSnippet = (): Snippet | undefined => {
  const saved_snippet_str = recoverBrowserSecurityError(() =>
    localStorage.getItem(SESSION_CONTENTS)
  );

  if (saved_snippet_str === null) return undefined;

  let saved_snippet: Snippet = JSON.parse(saved_snippet_str);
  const snippetLanguage =
    VISIBLE_LANGUAGES[saved_snippet.language] !== undefined
      ? saved_snippet.language
      : "js";

  saved_snippet.language = snippetLanguage;

  return saved_snippet;
};

/**
 * Highlights
 */

export const spanToHighlightRange = (start: CodeLoc, end: CodeLoc): IRange => ({
  startLineNumber: start.line,
  startColumn: start.col ?? 0,
  endLineNumber: end.line,
  endColumn: end.col ?? Infinity,
});

export const makeHighlight = (
  r: Result,
  isError: boolean,
  type?: string,
  newEnd?: CodeLoc,
  configLocation?: PatternConfigLocation
): Highlight => {
  const endLine = r?.end?.line ?? r.start.line; // if no end.line, assume one line
  const range = spanToHighlightRange(r.start, {
    col: newEnd?.col ?? r.end.col,
    line: newEnd?.line ?? endLine,
  });
  return {
    range,
    isError,
    key: makeResultKey(r),
    type: type || r.severity || "unknown",
    group: r.id,
    startLine: r.start.line,
    endLine: newEnd ? newEnd.line : endLine,
    message: r.message,
    configLocation,
  };
};

/**
 * Simple editor
 */

const isSinglePattern = (pattern: string) => {
  return !pattern.includes("rules:");
};

export const tryGUI = (
  snippet: Readonly<Snippet>,
  rule: LegacyRule
): {
  success: boolean;
  newPattern?: string;
  newRule?: LegacyRule;
  newVisibilities?: RuleVisibilities;
} => {
  const language = snippet.language;
  if (isSinglePattern(snippet.pattern)) {
    let newRule = {
      ...DEFAULT_RULE(language),
    };
    newRule.patterns = [{ pattern: snippet.pattern, type: "pattern" }];
    newRule.message = "";
    newRule.fix = "";
    return {
      success: true,
      newPattern: yamlFromRule(newRule),
      newRule,
      newVisibilities: DEFAULT_RULE_VISIBILITY,
    };
  }

  const [newRule, wasLossless] = ruleFromYAML(snippet.pattern, rule, language);
  if (newRule && wasLossless) {
    const visibility = { ...DEFAULT_RULE_VISIBILITY };
    if (newRule.fix !== "") visibility.fix = true;
    if (newRule.message !== "") visibility.message = true;
    return {
      success: true,
      newPattern: snippet.pattern,
      newRule,
      newVisibilities: visibility,
    };
  } else {
    return {
      success: false,
    };
  }
};

export const deletePatternComponent = (index: number, rule: LegacyRule) => {
  let newRule = cloneDeep(rule);
  if (newRule.patterns.length > 1) newRule.patterns.splice(index, 1);
  return newRule;
};

export const deletePatternAutofix = (rule: LegacyRule) => {
  let newRule = cloneDeep(rule);
  newRule.fix = "";
  return rule;
};

export const addNewPattern = (rule: LegacyRule) => {
  let newRule = cloneDeep(rule);
  const newPatterns = newRule.patterns.concat({
    pattern: "",
    type: "pattern",
  });
  newRule.patterns = newPatterns;
  return newRule;
};

/**
 * Running editor
 */

export const run = (
  snippet: Snippet,
  rule: LegacyRule,
  useGUI: boolean,
  version?: string
): Promise<{ result: RunResult; newRule: LegacyRule }> => {
  let newRule = rule;
  let yamlDoc: yaml.Document.Parsed;
  if (useGUI) {
    // if in simple mode, get the pattern to run from the rule
    snippet.pattern = yamlFromRule(rule);
    // ruleFromYaml ensures the pattern-builder elements are connected to yaml paths
    const [yamlRule, wasLossless] = ruleFromYAML(snippet.pattern, rule);
    if (!yamlRule || !wasLossless) {
      console.error("Pattern builder is not rebuildable");
    }
    newRule = yamlRule!;
  }

  try {
    yamlDoc = validateYaml(snippet.pattern);
  } catch ({ message }) {
    const result: RunResult = {
      ...EMPTY_RUN_RESULT,
      errors: {
        internalErrors: [`Could not parse pattern:\n${message}`],
        parseCodeErrors: [],
        parsePatternErrors: [],
      },
    };
    const toReturn = { result, newRule };
    return Promise.resolve(toReturn);
  }

  snippet.target = snippet.target.replace(/\t/g, "    ");
  const errorReturn = { result: EMPTY_RUN_RESULT, newRule };
  if (snippet.pattern.trim().length === 0) {
    AppToaster.show({
      message: "You can't run semgrep with an empty pattern",
      intent: "danger",
    });
    return Promise.resolve(errorReturn);
  } else if (snippet.target.trim().length === 0) {
    AppToaster.show({
      message: "You can't run semgrep with an empty code block",
      intent: "danger",
    });
    return Promise.resolve(errorReturn);
  }
  return apiRun(snippet, !isSinglePattern(snippet.pattern), version)
    .then((data) => {
      const result: RunResult = {
        results: data.results,
        errors: data.errors,
        timing: data.timing,
        imageId: data.image_id,
        runTimeS: data.run_time_s,
        errorMsg: data.error_msg,
        errorType: data.error_type,
        numTestFailures: data.num_test_failures,
      };
      return { result, newRule };
    })
    .catch((err) => {
      const toReturn = {
        result: handleRunErrors(yamlDoc, snippet.pattern, err),
        newRule,
      };
      return Promise.resolve(toReturn);
    });
};

const handleRunErrors = (
  yamlDoc: yaml.Document.Parsed,
  pattern: string,
  err: any
): RunResult => {
  const errors: SemgrepErrors = {
    internalErrors: [],
    parseCodeErrors: [],
    parsePatternErrors: [],
  };
  let errorType: ErrorType = "other";
  if ("path" in err.body) {
    // This means the backend is returning a schema error
    // TODO: make this clearer in the returned backend object
    errors.parsePatternErrors = schemaErrorToParseError(err, pattern, yamlDoc);
    errorType = "pattern";
  } else {
    errors.internalErrors = [err.body.error];
  }
  return {
    ...EMPTY_RUN_RESULT,
    errors,
    errorMsg: err.body.error,
    errorType: errorType,
  };
};

/**
 * For saving
 */

export const toTestCaseHighlights = (results: Result[]): TestCaseHighlight[] =>
  results.map((r) => ({
    start: r.start,
    end: r.end,
    message: r.message ?? "",
    fix: r.fixed_lines?.join("\n"),
  }));

/**
 * Applying fixes
 */

export const applyFix = (
  r: Result,
  target: string,
  highlighted: readonly Highlight[]
): { newTarget: string; newHighlights: readonly Highlight[] } => {
  // get the start and end points
  const lines = target.split("\n");
  if (r.fixed_lines && lines.length > 0) {
    const startLine = (r.start.line || 1) - 1; // start.line is 1 indexed
    const endLine = (r.end.line || 1) - 1; // end.line is 1 indexed
    // break into before, to modify, after
    const beforeLines = lines.slice(0, startLine);
    const afterLines = lines.slice(endLine + 1); // next line after end of match
    const modifiedLines = r.fixed_lines;
    // build new string
    const contentsAfterfix = [...beforeLines, ...modifiedLines, ...afterLines];
    const newEndLine = startLine + r.fixed_lines.length;
    const startCol = (r.start.col || 1) - 1; // start.col is 1 indexed
    const newEndCol =
      startCol + 1 + r.fixed_lines[r.fixed_lines.length - 1].length; // TODO
    const newTarget = contentsAfterfix.join("\n");
    // TODO update highlights
    const key = makeResultKey(r);
    const otherHighlights = highlighted.filter((hl) => hl.key !== key);
    const newHighlights = [
      ...otherHighlights,
      makeHighlight(r, false, "fix", { line: newEndLine, col: newEndCol }),
    ];
    return { newTarget, newHighlights };
  } else {
    console.warn("problem applying fixed lines", r.fixed_lines, target);
    return { newTarget: target, newHighlights: highlighted };
  }
};

/**
 * Auto adding tests
 */
const getCleanedLines = (
  target: string,
  language: string,
  results?: Result[]
): string => {
  const lines = target.split("\n");
  const comment = LANG_COMMENT_SYNTAX_MAP[language] || "//";
  // remove existing comments
  let cleanedLines = lines.map((line) => {
    const lc = line.toLowerCase();
    if (lc.includes(`${comment} ruleid:`) || lc.includes(`${comment}ruleid:`)) {
      // remove existing comments lines
      return undefined;
    }
    return line;
  });

  uniqBy(results, (r) => r.start.line).forEach((r) => {
    const matchLineNo = r.start.line - 1;
    const currentLine = cleanedLines[matchLineNo];
    let ws = "";
    if (currentLine !== undefined) {
      const whitespaceMatches = currentLine.match(/^(\s+)/);
      if (whitespaceMatches !== null) {
        ws = whitespaceMatches[1];
      }
    }
    // remove ok comments that are being replaced with rule id
    const currentMaybeComment = (
      cleanedLines[matchLineNo - 1] || ""
    ).toLowerCase();
    if (
      currentMaybeComment.includes(`${comment} ok`) ||
      currentMaybeComment.includes(`${comment}ok`)
    ) {
      cleanedLines[matchLineNo - 1] = undefined;
    }
    // now add the new comment
    const newTest = `${ws}${comment} ruleid:${r.id}`;
    const newLine = `${newTest}\n${currentLine || ""}`;
    cleanedLines[matchLineNo] = newLine;
  });

  // remove undefined lines and join
  return cleanedLines.filter((l) => l !== undefined).join("\n");
};

export const addAutoTests = (
  results: Result[],
  target: string,
  language: string
): string => {
  // remove undefined lines and join
  const newTarget = getCleanedLines(target, language, results);
  AppToaster.show({
    message: `Added True Postive unit tests to code snippet.`,
    intent: "success",
  });
  return newTarget;
};
